import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Dashboard.css';

function Dashboard() {
  const apiBase =
    typeof window !== 'undefined' && window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : '';

  const { currentUser, canAccessPage } = useAuth();
  const isDashboardAdmin = canAccessPage('admin');
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!currentUser) return;
    if (!isDashboardAdmin) {
      setMetrics(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const token = await currentUser.getIdToken();

      const response = await fetch(`${apiBase}/api/v1/dashboard/metrics`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const contentType = response.headers.get('content-type') || '';
      const isJson = contentType.includes('application/json');

      if (!response.ok) {
        if (isJson) {
          const errData = await response.json();
          throw new Error(errData.message || 'Failed to fetch dashboard metrics.');
        }
        const errorText = await response.text();
        throw new Error(`Failed to fetch dashboard metrics (status: ${response.status}). ${errorText.substring(0, 200)}...`);
      }

      const data = isJson ? await response.json() : null;
      if (!data || data.success !== true) {
        throw new Error('Failed to fetch dashboard metrics.');
      }

      setMetrics(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, apiBase, isDashboardAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const statCards = useMemo(() => {
    const ledgerToday = metrics?.ledger?.today || {};
    const ledgerLast7 = metrics?.ledger?.last7Days || {};

    const cards = [
      {
        title: 'Orders Created',
        today: metrics?.orders?.today ?? 0,
        last7: metrics?.orders?.last7Days ?? 0,
        tone: 'ready-to-ship',
      },
      {
        title: 'New Stock',
        today: ledgerToday.NEW_STOCK ?? 0,
        last7: ledgerLast7.NEW_STOCK ?? 0,
        tone: 'full-in-stock',
      },
      {
        title: 'Used',
        today: ledgerToday.SALE ?? 0,
        last7: ledgerLast7.SALE ?? 0,
        tone: 'partially-used',
      },
      {
        title: 'Restock / Return',
        today: (ledgerToday.RETURN ?? 0) + (ledgerToday.RESTOCK ?? 0),
        last7: (ledgerLast7.RETURN ?? 0) + (ledgerLast7.RESTOCK ?? 0),
        tone: 'partial-in-stock',
      },
      {
        title: 'Adjustments',
        today: ledgerToday.ADJUSTMENT ?? 0,
        last7: ledgerLast7.ADJUSTMENT ?? 0,
        tone: 'out-of-stock',
      },
    ];

    return cards;
  }, [metrics]);

  const actionNeeded = metrics?.actionNeeded || null;
  const topSkus = metrics?.topSkus || null;
  const system = metrics?.system || null;
  const automation = metrics?.automation || null;
  const formatTs = (value) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'N/A';
    return d.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  // These stats are no longer available directly on the dashboard.
  // They could be fetched from their own summary endpoints if needed.
  // For now, we will comment them out to avoid breaking the UI.
  // const totalInventoryLength = allInventoryPieces.reduce((sum, piece) => sum + piece.length, 0);
  // const totalInventoryPieces = allInventoryPieces.length;

  if (isLoading) {
    return <div className="dashboard-container"><h1>Loading Dashboard...</h1></div>;
  }

  if (error) {
    return <div className="dashboard-container"><h1>Error: {error}</h1><p>Could not fetch data from the backend. Is the backend server running?</p></div>;
  }

  if (!isDashboardAdmin) {
    return (
      <div className="dashboard-container">
        <h1>Access Denied</h1>
        <p>This page is admin-only.</p>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Welcome, {currentUser?.email}!</p>
      </div>

      <div className="stats-grid">
        {statCards.map((card) => (
          <div key={card.title} className={`stat-card ${card.tone || ''}`}>
            <h3>{card.title}</h3>
            <div className="stat-split">
              <div className="stat-split-col">
                <div className="stat-split-label">Today</div>
                <div className="stat-split-value">{card.today}</div>
              </div>
              <div className="stat-split-col">
                <div className="stat-split-label">Last 7 days</div>
                <div className="stat-split-value">{card.last7}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-sections">
        <section className="dashboard-section">
          <div className="dashboard-section-header">
            <h2>Action Needed</h2>
            <div className="dashboard-section-actions">
              <Link className="dashboard-link-btn" to="/orders">Open Orders</Link>
              <Link className="dashboard-link-btn" to="/batches">Open Production</Link>
              <Link className="dashboard-link-btn" to="/inventory">Open Inventory</Link>
              <Link className="dashboard-link-btn" to="/admin/system-actions">System Actions</Link>
            </div>
          </div>

          <div className="dashboard-kpi-grid">
            <Link to="/orders" className="dashboard-kpi-card">
              <div className="dashboard-kpi-title">Restock Pending</div>
              <div className="dashboard-kpi-value">{actionNeeded?.restockPendingOrders ?? 0}</div>
              <div className="dashboard-kpi-sub">Orders</div>
            </Link>
            <Link to="/orders" className="dashboard-kpi-card">
              <div className="dashboard-kpi-title">Awaiting Batch</div>
              <div className="dashboard-kpi-value">{actionNeeded?.awaitingBatchLines ?? 0}</div>
              <div className="dashboard-kpi-sub">Line items</div>
            </Link>
            <Link to="/batches" className="dashboard-kpi-card">
              <div className="dashboard-kpi-title">Batches Pending Receive</div>
              <div className="dashboard-kpi-value">{actionNeeded?.batchesPendingReceive ?? 0}</div>
              <div className="dashboard-kpi-sub">{actionNeeded?.pendingMeters ?? 0} m pending</div>
            </Link>
          </div>
        </section>

        <section className="dashboard-section">
          <div className="dashboard-section-header">
            <h2>Top SKUs (Last 7 days)</h2>
          </div>

          <div className="dashboard-top-grid">
            <div className="dashboard-top-card">
              <h3>Used</h3>
              <ul>
                {(topSkus?.used || []).slice(0, 8).map((x) => (
                  <li key={`used-${x.sku}`}>
                    <span className="sku">{x.sku}</span>
                    <span className="count">{x.used}</span>
                  </li>
                ))}
                {(topSkus?.used || []).length === 0 && <li className="empty">No activity</li>}
              </ul>
            </div>
            <div className="dashboard-top-card">
              <h3>New Stock</h3>
              <ul>
                {(topSkus?.newStock || []).slice(0, 8).map((x) => (
                  <li key={`new-${x.sku}`}>
                    <span className="sku">{x.sku}</span>
                    <span className="count">{x.newStock}</span>
                  </li>
                ))}
                {(topSkus?.newStock || []).length === 0 && <li className="empty">No activity</li>}
              </ul>
            </div>
            <div className="dashboard-top-card">
              <h3>Restock / Return</h3>
              <ul>
                {(topSkus?.restock || []).slice(0, 8).map((x) => (
                  <li key={`restock-${x.sku}`}>
                    <span className="sku">{x.sku}</span>
                    <span className="count">{x.restock}</span>
                  </li>
                ))}
                {(topSkus?.restock || []).length === 0 && <li className="empty">No activity</li>}
              </ul>
            </div>
          </div>
        </section>

        <section className="dashboard-section">
          <div className="dashboard-section-header">
            <h2>System Health</h2>
          </div>

          <div className="dashboard-system-grid">
            <div className="dashboard-system-card">
              <h3>Zoho Sync</h3>
              <div className="meta">
                <div><span className="k">Status</span><span className="v">{system?.zohoSync?.status || 'N/A'}</span></div>
                <div><span className="k">Time</span><span className="v">{system?.zohoSync?.timestamp || 'N/A'}</span></div>
              </div>
              {system?.zohoSync?.message && <div className="msg">{system.zohoSync.message}</div>}
            </div>
            <div className="dashboard-system-card">
              <h3>Shopify Sync</h3>
              <div className="meta">
                <div><span className="k">Status</span><span className="v">{system?.shopifySync?.status || 'N/A'}</span></div>
                <div><span className="k">Time</span><span className="v">{system?.shopifySync?.timestamp || 'N/A'}</span></div>
              </div>
              {system?.shopifySync?.message && <div className="msg">{system.shopifySync.message}</div>}
            </div>
          </div>
        </section>

        <section className="dashboard-section">
          <div className="dashboard-section-header">
            <h2>Automation Health</h2>
            <div className="dashboard-section-actions">
              <Link className="dashboard-link-btn" to="/collections-automation">Open Collections</Link>
              <Link className="dashboard-link-btn" to="/products">Open Products</Link>
            </div>
          </div>

          <div className="dashboard-system-grid">
            <div className="dashboard-system-card">
              <h3>Stock Publish</h3>
              <div className="meta">
                <div><span className="k">Status</span><span className="v">{automation?.steps?.stockPublish?.status || 'N/A'}</span></div>
                <div><span className="k">Updated</span><span className="v">{formatTs(automation?.steps?.stockPublish?.updatedAt)}</span></div>
                <div><span className="k">Week</span><span className="v">{automation?.steps?.stockPublish?.weekKey || 'N/A'}</span></div>
              </div>
              {automation?.steps?.stockPublish?.lastError && <div className="msg">{automation.steps.stockPublish.lastError}</div>}
            </div>

            <div className="dashboard-system-card">
              <h3>Price + Labels</h3>
              <div className="meta">
                <div><span className="k">Status</span><span className="v">{automation?.steps?.priceLabels?.status || 'N/A'}</span></div>
                <div><span className="k">Updated</span><span className="v">{formatTs(automation?.steps?.priceLabels?.updatedAt)}</span></div>
                <div><span className="k">Week</span><span className="v">{automation?.steps?.priceLabels?.weekKey || 'N/A'}</span></div>
              </div>
              {automation?.steps?.priceLabels?.lastError && <div className="msg">{automation.steps.priceLabels.lastError}</div>}
            </div>

            <div className="dashboard-system-card">
              <h3>Full Collections</h3>
              <div className="meta">
                <div><span className="k">Status</span><span className="v">{automation?.steps?.fullCollections?.status || 'N/A'}</span></div>
                <div><span className="k">Updated</span><span className="v">{formatTs(automation?.steps?.fullCollections?.updatedAt)}</span></div>
                <div><span className="k">Run</span><span className="v">{(automation?.steps?.fullCollections?.okCount ?? 0)}/{(automation?.steps?.fullCollections?.total ?? 0)} ok</span></div>
              </div>
              {automation?.steps?.fullCollections?.lastMessage && <div className="msg">{automation.steps.fullCollections.lastMessage}</div>}
            </div>

            <div className="dashboard-system-card">
              <h3>Reorder-only</h3>
              <div className="meta">
                <div><span className="k">Status</span><span className="v">{automation?.steps?.reorderOnlyCollections?.status || 'N/A'}</span></div>
                <div><span className="k">Updated</span><span className="v">{formatTs(automation?.steps?.reorderOnlyCollections?.updatedAt)}</span></div>
                <div><span className="k">Run</span><span className="v">{(automation?.steps?.reorderOnlyCollections?.okCount ?? 0)}/{(automation?.steps?.reorderOnlyCollections?.total ?? 0)} ok</span></div>
              </div>
              {automation?.steps?.reorderOnlyCollections?.lastMessage && <div className="msg">{automation.steps.reorderOnlyCollections.lastMessage}</div>}
            </div>
          </div>

          <div className="dashboard-top-grid dashboard-top-grid-wide">
            <div className="dashboard-top-card">
              <h3>Collection Freshness</h3>
              <ul>
                <li><span className="sku">Targeted + Enabled</span><span className="count">{automation?.collections?.targetedEnabledCount ?? 0}</span></li>
                <li><span className="sku">Full Automation</span><span className="count">{automation?.collections?.fullAutomationCount ?? 0}</span></li>
                <li><span className="sku">Reorder-only</span><span className="count">{automation?.collections?.reorderOnlyCount ?? 0}</span></li>
                <li><span className="sku">Applied OK</span><span className="count">{automation?.collections?.appliedOkCount ?? 0}</span></li>
                <li><span className="sku">Applied Error</span><span className="count">{automation?.collections?.appliedErrorCount ?? 0}</span></li>
                <li><span className="sku">Stale (&gt;{automation?.collections?.staleThresholdDays ?? 8}d)</span><span className="count">{automation?.collections?.staleCount ?? 0}</span></li>
              </ul>
            </div>

            <div className="dashboard-top-card">
              <h3>Top Changed Collections</h3>
              <ul>
                {(automation?.collections?.topChangedCollections || []).slice(0, 8).map((x) => (
                  <li key={`chg-${x.id}`}>
                    <span className="sku">{x.title}</span>
                    <span className="count">{x.changed}</span>
                  </li>
                ))}
                {(automation?.collections?.topChangedCollections || []).length === 0 && <li className="empty">No recent changes</li>}
              </ul>
            </div>

            <div className="dashboard-top-card">
              <h3>Stale Collections</h3>
              <ul>
                {(automation?.collections?.staleCollections || []).slice(0, 8).map((x) => (
                  <li key={`stale-${x.id}`}>
                    <span className="sku">{x.title}</span>
                    <span className="count">{x.lastAppliedAt ? formatTs(x.lastAppliedAt).split(',')[0] : 'never'}</span>
                  </li>
                ))}
                {(automation?.collections?.staleCollections || []).length === 0 && <li className="empty">No stale collections</li>}
              </ul>
            </div>
          </div>

          <div className="dashboard-table-wrap">
            <h3 className="dashboard-table-title">Recent Price+Labels Runs</h3>
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Updated</th>
                  <th>Status</th>
                  <th>Pricing</th>
                  <th>Labels</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {(automation?.recentPriceLabelsRuns || []).map((run) => (
                  <tr key={run.id}>
                    <td>{formatTs(run.updatedAt)}</td>
                    <td>{run.status || 'N/A'}</td>
                    <td>{run.pricingApplied}/{run.pricingTotal}</td>
                    <td>{run.labelsApplied}/{run.labelsTotal}</td>
                    <td>{run.lastError || '—'}</td>
                  </tr>
                ))}
                {(automation?.recentPriceLabelsRuns || []).length === 0 && (
                  <tr>
                    <td colSpan="5">No recent runs</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>

    </div>
  );
}

export default Dashboard;
